<!--
  ╔═══════════════════════════════════════════════════════════════════════════════╗
  ║  ORG BOARD NODE — Unified ECO Tree Node Component                           ║
  ║                                                                             ║
  ║  This template renders a single node (card) on the Organization Board.      ║
  ║  It replaces the previous 3 separate components:                            ║
  ║    - no-user.component.ts   (0 users assigned)                              ║
  ║    - one-user.component.ts  (1 user assigned)                               ║
  ║    - many-users.component.ts (2+ users assigned)                            ║
  ║                                                                             ║
  ║  BUSINESS RULES:                                                            ║
  ║    • Only "post" parts can be directly staffed with users                   ║
  ║    • Containers (executive, division, department, section, unit) are         ║
  ║      NEVER directly assigned users — they only display nested Posts         ║
  ║    • HFA (Held From Above) only applies to Posts with no own users          ║
  ║    • VFP (Valuable Final Product) applies to all part types                 ║
  ║                                                                             ║
  ║  TEMPLATE SECTIONS (in order):                                              ║
  ║    A. ECO-Level User Display — varies by userDisplayMode (the ONLY          ║
  ║       difference between the old 3 components)                              ║
  ║    B. Editing User Dropzone — CDK drag-drop target for user assignment      ║
  ║    C. Posts In Parent — posts directly under division/executive              ║
  ║    D. Sections In Department — nested hierarchy:                            ║
  ║       section → postsInSection, unitsInSection → postsInUnit                ║
  ║    E. Node-Level VFP — bottom section of each card                          ║
  ║                                                                             ║
  ║  VFP VISIBILITY RULES (updated 2026-03-27):                                 ║
  ║    All VFP elements use *ngIf="(part?.vfp || isVfpEditingOn)":               ║
  ║    • Edit mode ON: VFP always in DOM, shows placeholder 'Click to add       ║
  ║      VFP...', clickable to edit inline (textarea + Ctrl+Enter to save)      ║
  ║    • Edit mode OFF + VFP has text: Element in DOM, CSS hides by default     ║
  ║      (opacity:0, max-height:0), revealed on hover via parent hover area     ║
  ║      — read-only, NOT clickable or editable                                 ║
  ║    • Edit mode OFF + VFP empty: Element removed from DOM entirely,          ║
  ║      nothing shown on hover (no 'Not defined' placeholder)                  ║
  ║    CSS classes: .sub-part-vfp (sections/units/posts),                       ║
  ║                 .vfp-hover-only (node-level non-departments)                ║
  ║    Hover selectors: .section-hover-area:hover > .sub-part-vfp              ║
  ║                      .unit-hover-area:hover > .sub-part-vfp                ║
  ║                      .post-hover-area:hover > .sub-part-vfp                ║
  ║                      .org-position-card:hover .vfp-hover-only              ║
  ║                                                                             ║
  ║  HFA (HELD FROM ABOVE) NAVIGATION (updated 2026-03-27):                     ║
  ║    The blue arrow_circle_down icon emits navigateToHfaOrigin with the       ║
  ║    post's heldFromAbovePartKey. The parent handler onNavigateToHfaOrigin()  ║
  ║    searches: (1) ancestor chain, (2) all top-level ECO nodes, (3) sub-parts ║
  ║    (postsInParent, sectionsInDepartment[].postsInSection,                   ║
  ║     unitsInSection[].postsInUnit) — because the HFA origin is usually a    ║
  ║    post nested inside another ECO node, not a top-level node itself.        ║
  ║    When found, it scrolls to the containing ECO node and opens the sidebar. ║
  ║                                                                             ║
  ║  HEIGHT CALCULATION DEPENDENCY:                                             ║
  ║    The organization-board.component.ts → updateNodeDimensions() uses        ║
  ║    pixel constants derived from elements in this template. If you add,      ║
  ║    remove, or resize elements, update the PX_* constants to match.          ║
  ║    See: docs/ORG-BOARD-HEIGHT-CALCULATION-SPEC.md                           ║
  ╚═══════════════════════════════════════════════════════════════════════════════╝
-->

<mat-card
    class="org-position-card"
    [attr.data-part-type]="nodeAsAny?.data?.partType"
    [attr.data-card-key]="nodeAsAny?.data?.partKey || nodeAsAny?.data?.key"
    (click)="isDragNavDisabled || isNavigatingWithDrag ? '' : selectSiblingNode.emit(node)"
>
    <!-- ECO card is a container — NOT a droppable-part. Only nested Posts have droppable-part class. -->

    <!-- ═══════════════════════════════════════════════════════════════════════════
       SECTION A: ECO-Level User Display
       ═══════════════════════════════════════════════════════════════════════════
       This is the ONLY section that differs between user display modes.
       Only shown when the node is a "post" (containers never display ECO-level users).

       Modes:
         NO_USERS        → "Position is currently unoccupied" (italic text)
         ONE_USER         → Single avatar + full name
         MULTIPLE_USERS   → First user avatar + "and X other(s)"
       ═══════════════════════════════════════════════════════════════════════════ -->
    <ng-container *ngIf="nodeAsAny?.data?.partType === 'post'" [ngSwitch]="effectiveUserDisplayMode">
        <!-- A1: No users — unoccupied position message -->
        <ng-container *ngSwitchCase="'NO_USERS'">
            <div class="ob-staff">
                <div class="user-list-container" style="padding: 8px 18px; opacity: 0.6; font-style: italic">
                    Org part is currently unoccupied.
                </div>
            </div>
        </ng-container>

        <!-- A2: One user — single avatar with full name -->
        <ng-container *ngSwitchCase="'ONE_USER'">
            <div
                class="ob-staff"
                [ngStyle]="
                    nodeAsAny?.data?.partType === 'department'
                        ? { 'border-bottom': 'solid 2px rgb(179, 179, 179)' }
                        : {}
                "
                [class.processing-user]="isProcessingUser(displayUserDetailArray[0]?.key, nodeAsAny?.data?.partKey)"
            >
                <span
                    class="grouped"
                    [class.ob-pending-row]="displayUserDetailArray[0]?.isPending"
                    style="
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        margin-left: 18px;
                        margin-top: 0.5rem;
                        margin-bottom: 0.5rem;
                    "
                >
                    <span style="display: flex; align-items: center">
                        <span *ngIf="displayUserDetailArray[0]?.isPending" class="ob-pending-avatar-wrap">
                            <img
                                class="ob-pending-avatar"
                                [class.ob-online]="isUserOnline(displayUserDetailArray[0])"
                                style="border-radius: 50%; object-fit: cover; flex-shrink: 0"
                                [src]="getUserAvatarUrl(displayUserDetailArray[0])"
                            />
                            <span *ngIf="isUserOnline(displayUserDetailArray[0])" class="ob-online-dot"></span>
                        </span>
                        <span *ngIf="!displayUserDetailArray[0]?.isPending" class="ob-avatar-wrap">
                            <img
                                [class.ob-online]="isUserOnline(displayUserDetailArray[0])"
                                style="
                                    width: 2.5rem;
                                    height: 2.5rem;
                                    border-radius: 50%;
                                    object-fit: cover;
                                    flex-shrink: 0;
                                "
                                [src]="getUserAvatarUrl(displayUserDetailArray[0])"
                            />
                            <span *ngIf="isUserOnline(displayUserDetailArray[0])" class="ob-online-dot"></span>
                        </span>
                        <span
                            class="clickable-span"
                            (click)="
                                openUserTemplate.emit({ user: displayUserDetailArray[0], node: node });
                                $event.stopPropagation()
                            "
                            style="
                                margin-left: 6px;
                                font-size: 24px;
                                font-weight: 500;
                                line-height: 1.2;
                                text-align: left;
                            "
                        >
                            {{ displayUserDetailArray[0].name || '' | titlecase }}
                            <span
                                *ngIf="
                                    displayUserDetailArray[0]?.isPending &&
                                    !displayUserDetailArray[0]?.awaitingAcceptance
                                "
                                class="ob-pending-badge"
                                matTooltip="Awaiting admin approval on this post"
                                >PENDING</span
                            >
                            <span
                                *ngIf="displayUserDetailArray[0]?.awaitingAcceptance"
                                class="ob-pending-badge ob-invited-badge"
                                matTooltip="Invited — awaiting user to accept the invitation. Admin cannot approve until accepted."
                                >INVITED</span
                            >
                        </span>
                    </span>
                    <span class="ob-user-actions">
                        <button
                            *ngIf="
                                isEditing &&
                                displayUserDetailArray[0]?.isPending &&
                                !displayUserDetailArray[0]?.awaitingAcceptance
                            "
                            mat-icon-button
                            matTooltip="Approve assignment"
                            style="
                                width: 24px;
                                height: 24px;
                                line-height: 24px;
                                min-width: 24px;
                                padding: 0;
                                background-color: #2e7d32;
                                border-radius: 50%;
                            "
                            (click)="
                                approveUser.emit({ user: displayUserDetailArray[0], node: node });
                                $event.stopPropagation()
                            "
                        >
                            <mat-icon style="font-size: 18px; width: 18px; height: 18px; color: white">check</mat-icon>
                        </button>
                        <button
                            *ngIf="isEditing"
                            mat-icon-button
                            matTooltip="Remove staff member from post"
                            style="
                                width: 24px;
                                height: 24px;
                                line-height: 24px;
                                min-width: 24px;
                                padding: 0;
                                background-color: #c62828;
                                border-radius: 50%;
                            "
                            (click)="
                                onRemoveUser(displayUserDetailArray[0], nodeAsAny?.data?.partKey);
                                $event.stopPropagation()
                            "
                        >
                            <mat-icon style="font-size: 18px; width: 18px; height: 18px; color: white">{{
                                displayUserDetailArray[0]?.isPending ? 'close' : 'remove'
                            }}</mat-icon>
                        </button>
                    </span>
                </span>
            </div>
        </ng-container>

        <!-- A3: Multiple users — first user avatar with "and X other(s)" count -->
        <ng-container *ngSwitchCase="'MULTIPLE_USERS'">
            <div
                class="ob-staff"
                [ngStyle]="
                    nodeAsAny?.data?.partType === 'department'
                        ? { 'border-bottom': 'solid 2px rgb(179, 179, 179)' }
                        : {}
                "
                [class.processing-user]="isProcessingUser(displayUserDetailArray[0]?.key, nodeAsAny?.data?.partKey)"
            >
                <span
                    class="grouped"
                    [class.ob-pending-row]="displayUserDetailArray[0]?.isPending"
                    style="
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        margin-left: 18px;
                        margin-top: 0.5rem;
                        margin-bottom: 0.5rem;
                    "
                >
                    <span style="display: flex; align-items: center">
                        <span *ngIf="displayUserDetailArray[0]?.isPending" class="ob-pending-avatar-wrap">
                            <img
                                class="ob-pending-avatar"
                                [class.ob-online]="isUserOnline(displayUserDetailArray[0])"
                                style="object-fit: cover; border-radius: 50%; flex-shrink: 0"
                                [src]="getUserAvatarUrl(displayUserDetailArray[0])"
                            />
                            <span *ngIf="isUserOnline(displayUserDetailArray[0])" class="ob-online-dot"></span>
                        </span>
                        <span *ngIf="!displayUserDetailArray[0]?.isPending" class="ob-avatar-wrap">
                            <img
                                [class.ob-online]="isUserOnline(displayUserDetailArray[0])"
                                style="
                                    width: 2.5rem;
                                    height: 2.5rem;
                                    object-fit: cover;
                                    border-radius: 50%;
                                    flex-shrink: 0;
                                "
                                [src]="getUserAvatarUrl(displayUserDetailArray[0])"
                            />
                            <span *ngIf="isUserOnline(displayUserDetailArray[0])" class="ob-online-dot"></span>
                        </span>
                        <span
                            class="clickable-span"
                            (click)="
                                openUserTemplate.emit({ user: displayUserDetailArray[0], node: node });
                                $event.stopPropagation()
                            "
                            style="
                                margin-left: 6px;
                                font-size: 24px;
                                font-weight: 500;
                                line-height: 1.2;
                                text-align: left;
                            "
                        >
                            {{ displayUserDetailArray[0].name || '' | titlecase }}
                            <span
                                *ngIf="
                                    displayUserDetailArray[0]?.isPending &&
                                    !displayUserDetailArray[0]?.awaitingAcceptance
                                "
                                class="ob-pending-badge"
                                matTooltip="Awaiting admin approval on this post"
                                >PENDING</span
                            >
                            <span
                                *ngIf="displayUserDetailArray[0]?.awaitingAcceptance"
                                class="ob-pending-badge ob-invited-badge"
                                matTooltip="Invited — awaiting user to accept the invitation. Admin cannot approve until accepted."
                                >INVITED</span
                            >
                            <small
                                >and {{ displayUserDetailArray.length - 1 }} other{{
                                    displayUserDetailArray.length - 1 > 1 ? 's' : ''
                                }}</small
                            >
                        </span>
                    </span>
                    <span class="ob-user-actions">
                        <button
                            *ngIf="
                                isEditing &&
                                displayUserDetailArray[0]?.isPending &&
                                !displayUserDetailArray[0]?.awaitingAcceptance
                            "
                            mat-icon-button
                            matTooltip="Approve assignment"
                            style="
                                width: 24px;
                                height: 24px;
                                line-height: 24px;
                                min-width: 24px;
                                padding: 0;
                                background-color: #2e7d32;
                                border-radius: 50%;
                            "
                            (click)="
                                approveUser.emit({ user: displayUserDetailArray[0], node: node });
                                $event.stopPropagation()
                            "
                        >
                            <mat-icon style="font-size: 18px; width: 18px; height: 18px; color: white">check</mat-icon>
                        </button>
                        <button
                            *ngIf="isEditing"
                            mat-icon-button
                            matTooltip="Remove staff member from post"
                            style="
                                width: 24px;
                                height: 24px;
                                line-height: 24px;
                                min-width: 24px;
                                padding: 0;
                                background-color: #c62828;
                                border-radius: 50%;
                            "
                            (click)="
                                onRemoveUser(displayUserDetailArray[0], nodeAsAny?.data?.partKey);
                                $event.stopPropagation()
                            "
                        >
                            <mat-icon style="font-size: 18px; width: 18px; height: 18px; color: white">{{
                                displayUserDetailArray[0]?.isPending ? 'close' : 'remove'
                            }}</mat-icon>
                        </button>
                    </span>
                </span>
            </div>
        </ng-container>
    </ng-container>

    <mat-card-content>
        <!-- ═══════════════════════════════════════════════════════════════════════════
         SECTION B: Editing User Dropzone (CDK Drag-Drop)
         ═══════════════════════════════════════════════════════════════════════════
         Only shown when:
           1. We are in edit mode (isEditing = true)
           2. The node is a "post" (containers can't be staffed directly)

         Renders the list of currently assigned users with:
           - Clickable names to open user details sidebar
           - Remove button to unassign the user from the post
           - CDK drag/drop support for user reassignment
         ═══════════════════════════════════════════════════════════════════════════ -->
        <div class="ob-staff" *ngIf="isEditing && nodeAsAny?.data?.partType === 'post'">
            <ul
                class="clickable-li"
                cdkDropList
                [cdkDropListDisabled]="!isEditing"
                (cdkDropListDropped)="templateDrop.emit({ event: $event, node: node })"
                [cdkDropListConnectedTo]="dropLists"
                [cdkDropListData]="data"
            >
                <li
                    *ngFor="let user of userDetailArray"
                    (click)="openUserTemplate.emit({ user: user, node: node }); $event.stopPropagation()"
                    cdkDrag
                    [cdkDragData]="user"
                    [cdkDragDisabled]="!isEditing || user?.isPending || user?.awaitingAcceptance"
                    (cdkDragEnded)="stateChange.emit(false); dragEnded.emit(node)"
                    (cdkDragStarted)="stateChange.emit(true); dragStarted.emit(node)"
                    [class.ob-pending-row]="user?.isPending"
                    [class.processing-user]="isProcessingUser(user?.key, node?.data?.key || '')"
                    style="display: flex; align-items: center; justify-content: space-between"
                >
                    <span style="display: flex; align-items: center; gap: 4px">
                        {{ user.name | titlecase }}
                        <span
                            *ngIf="user?.isPending && !user?.awaitingAcceptance"
                            class="ob-pending-badge"
                            matTooltip="Awaiting admin approval on this post"
                            >PENDING</span
                        >
                        <span
                            *ngIf="user?.awaitingAcceptance"
                            class="ob-pending-badge ob-invited-badge"
                            matTooltip="Invited — awaiting user to accept the invitation. Admin cannot approve until accepted."
                            >INVITED</span
                        >
                    </span>

                    <div style="display: flex; align-items: center; gap: 4px">
                        <!-- Approve pending user button -->
                        <button
                            *ngIf="isEditing && user?.isPending && !user?.awaitingAcceptance && node?.data?.key"
                            mat-icon-button
                            matTooltip="Approve assignment"
                            style="
                                width: 24px;
                                height: 24px;
                                line-height: 24px;
                                min-width: 24px;
                                padding: 0;
                                background-color: #2e7d32;
                                border-radius: 50%;
                            "
                            (click)="approveUser.emit({ user: user, node: node, post: null }); $event.stopPropagation()"
                        >
                            <mat-icon style="font-size: 18px; width: 18px; height: 18px; color: white">check</mat-icon>
                        </button>
                        <!-- Remove user button (only in edit mode) -->
                        <button
                            *ngIf="isEditing && node?.data?.key"
                            mat-icon-button
                            matTooltip="Remove staff member from org part"
                            style="
                                width: 24px;
                                height: 24px;
                                line-height: 24px;
                                min-width: 24px;
                                padding: 0;
                                background-color: #c62828;
                                border-radius: 50%;
                            "
                            (click)="onRemoveUser(user, node!.data!.key); $event.stopPropagation()"
                        >
                            <mat-icon style="font-size: 18px; width: 18px; height: 18px; color: white">{{
                                user?.isPending ? 'close' : 'remove'
                            }}</mat-icon>
                        </button>
                    </div>

                    <div class="drag-placeholder" *cdkDragPlaceholder>
                        <span>{{ user.name | titlecase }}</span>
                    </div>
                </li>
            </ul>
        </div>

        <!-- ═══════════════════════════════════════════════════════════════════════════
         SECTION C: Posts In Parent (postsInParent)
         ═══════════════════════════════════════════════════════════════════════════
         Posts that are direct children of a division or executive node.
         Each post is a droppable target (class="droppable-part") for user drag-drop.

         For each post:
           - "Post" badge + clickable name
           - Edit menu (add sibling, edit, delete)
           - User avatars (if users assigned)
           - HFA indication (if post has no own users but inherits from above)

         NOTE: Posts with `headerRole` (head/deputy/assistant) are partitioned out
         by the builder and rendered INSIDE the coloured card title bar
         (`.above-card` / `.above-card2`) by `organization-board.component.html`,
         not here. This block only renders body posts.
         ═══════════════════════════════════════════════════════════════════════════ -->
        <!-- ────────────────────────────────────────────────────────────────────────
         Outer body-children dispatch — renders posts + sections (or posts + units)
         in path order, honouring the order chosen in the editor / Reorder Children.
         The legacy `postsInParent` / `sectionsInDepartment` split always grouped
         all posts before all sections regardless of intent. The per-item templates
         (#postInParentTpl, #sectionInDeptTpl, #postInSectionTpl, #unitInSectionTpl)
         live below in their original locations (formerly SECTION C / SECTION D).
         ─────────────────────────────────────────────────────────────────────── -->
        <div
            *ngIf="nodeAsAny?.data?.bodyChildren?.length"
            style="margin-top: 0.25rem; display: flex; flex-direction: column"
        >
            <ng-container *ngFor="let child of nodeAsAny.data.bodyChildren">
                <ng-container
                    *ngIf="child.partType === 'post'"
                    [ngTemplateOutlet]="postInParentTpl"
                    [ngTemplateOutletContext]="{ $implicit: child }"
                ></ng-container>
                <ng-container
                    *ngIf="child.partType === 'section'"
                    [ngTemplateOutlet]="sectionInDeptTpl"
                    [ngTemplateOutletContext]="{ $implicit: child }"
                ></ng-container>
            </ng-container>
        </div>

        <!-- Post directly under a department/division/executive.
         Styled like the head-post chip in the card title bar
         (#headerStripTpl): off-white background tinted with the parent's
         brand colour, wider, generously rounded, with a subtle border +
         shadow. This visually disambiguates body posts (which belong to
         the department itself) from posts living inside a sibling section. -->
        <ng-template #postInParentTpl let-post>
            <div style="display: flex; flex-direction: column; align-items: stretch">
                <div
                    class="user-list-container droppable-part ob-pip-card"
                    [class.deleting-part]="isDeleting(post.partKey || post.key)"
                    [style.background]="getLightTint(node?.bc ?? undefined, 0.85) || 'rgba(255,255,255,0.95)'"
                    [style.border]="'1px solid ' + (node?.bc || 'rgba(0,0,0,0.12)')"
                    style="
                        border-radius: 14px;
                        width: 100%;
                        align-self: stretch;
                        margin: 6px 0;
                        padding: 6px 8px;
                        box-sizing: border-box;
                        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
                    "
                    [attr.data-part-key]="post.key"
                    [attr.data-part-type]="'post'"
                    [attr.data-part-name]="post.name"
                    [attr.data-parent-dept-key]="node?.data?.key"
                >
                    <!-- Post header: badge + name + context menu -->
                    <div
                        style="
                            display: flex;
                            align-items: center;
                            gap: 6px;
                            font-size: 24px;
                            font-weight: 700;
                            line-height: 1.2;
                        "
                    >
                        <span
                            class="clickable-span"
                            style="flex: 1; text-align: center"
                            (click)="
                                openPositionTemplate.emit({ data: post, parentNode: node }); $event.stopPropagation()
                            "
                            >{{ post.name }}</span
                        >
                        <button
                            *ngIf="isEditing"
                            mat-icon-button
                            [matMenuTriggerFor]="postInParentMenu"
                            [disabled]="isDeleting(post.partKey || post.key)"
                            class="ob-menu-btn"
                            style="width: 24px; height: 24px; min-width: 24px; padding: 0; margin-left: auto"
                            (click)="$event.stopPropagation()"
                        >
                            <mat-icon style="font-size: 16px; width: 16px; height: 16px">more_vert</mat-icon>
                        </button>
                        <mat-menu #postInParentMenu="matMenu">
                            <!-- TRIAL: "Add Sibling Post" temporarily hidden from the post menu.
                   Uncomment the button + the trailing <mat-divider> below to restore it.
              <button mat-menu-item (click)="onAddSiblingPost(post)">
                <mat-icon color="primary">add</mat-icon>
                <span>Add Sibling Post</span>
              </button>
              <mat-divider></mat-divider>
              -->
                            <button mat-menu-item (click)="onEditPost(post)">
                                <mat-icon>edit</mat-icon>
                                <span>Edit Post</span>
                            </button>
                            <button mat-menu-item *ngIf="!hideStaff" (click)="bulkAssignStaff.emit(post)">
                                <mat-icon color="primary">group_add</mat-icon>
                                <span>Assign Staff to Post</span>
                            </button>
                            <button mat-menu-item (click)="dialogService.openPostHatsManagerDialog(post)">
                                <mat-icon style="color: var(--entity-color-hats, #1093ad)">school</mat-icon>
                                <span>Manage Post Hats</span>
                            </button>
                            <button mat-menu-item (click)="onDeletePost(post)">
                                <mat-icon color="warn">delete</mat-icon>
                                <span>Delete Post</span>
                            </button>
                        </mat-menu>
                    </div>

                    <!-- Post users (when users are directly assigned) -->
                    <div
                        class="ob-staff"
                        *ngIf="getVisibleUsersForPost(post).length > 0"
                        style="margin-top: 2px; padding-left: 0"
                    >
                        <div
                            *ngFor="let user of getVisibleUsersForPost(post)"
                            [class.ob-pending-row]="user?.isPending"
                            [class.processing-user]="isProcessingUser(user?.key, post.partKey || post.key)"
                            style="display: flex; align-items: center; justify-content: space-between; padding: 2px 0"
                        >
                            <span style="display: flex; align-items: center">
                                <span *ngIf="user?.isPending" class="ob-pending-avatar-wrap">
                                    <img
                                        [src]="getUserAvatarUrl(user)"
                                        class="ob-pending-avatar"
                                        [class.ob-online]="isUserOnline(user)"
                                        style="object-fit: cover; border-radius: 50%; flex-shrink: 0"
                                    />
                                    <span *ngIf="isUserOnline(user)" class="ob-online-dot"></span>
                                </span>
                                <span *ngIf="!user?.isPending" class="ob-avatar-wrap">
                                    <img
                                        [src]="getUserAvatarUrl(user)"
                                        [class.ob-online]="isUserOnline(user)"
                                        style="
                                            width: 2.5rem;
                                            height: 2.5rem;
                                            object-fit: cover;
                                            border-radius: 50%;
                                            flex-shrink: 0;
                                        "
                                    />
                                    <span *ngIf="isUserOnline(user)" class="ob-online-dot"></span>
                                </span>
                                <span
                                    class="clickable-span"
                                    (click)="
                                        openUserTemplate.emit({ user: user, node: node }); $event.stopPropagation()
                                    "
                                    style="
                                        margin-left: 6px;
                                        font-size: 24px;
                                        font-weight: 500;
                                        line-height: 1.2;
                                        text-align: left;
                                    "
                                >
                                    {{ user.name | titlecase }}
                                    <span
                                        *ngIf="user?.isPending && !user?.awaitingAcceptance"
                                        class="ob-pending-badge"
                                        matTooltip="Awaiting admin approval on this post"
                                        >Pending</span
                                    >
                                    <span
                                        *ngIf="user?.awaitingAcceptance"
                                        class="ob-pending-badge ob-invited-badge"
                                        matTooltip="Invited — awaiting user to accept the invitation. Admin cannot approve until accepted."
                                        >Invited</span
                                    >
                                </span>
                            </span>
                            <span class="ob-user-actions">
                                <button
                                    *ngIf="isEditing && user?.isPending && !user?.awaitingAcceptance"
                                    mat-icon-button
                                    matTooltip="Approve assignment"
                                    style="
                                        width: 24px;
                                        height: 24px;
                                        line-height: 24px;
                                        min-width: 24px;
                                        padding: 0;
                                        background-color: #2e7d32;
                                        border-radius: 50%;
                                    "
                                    (click)="
                                        approveUser.emit({ user: user, node: node, post: post });
                                        $event.stopPropagation()
                                    "
                                >
                                    <mat-icon style="font-size: 18px; width: 18px; height: 18px; color: white"
                                        >check</mat-icon
                                    >
                                </button>
                                <button
                                    *ngIf="isEditing"
                                    mat-icon-button
                                    matTooltip="Remove staff member from post"
                                    style="
                                        width: 24px;
                                        height: 24px;
                                        line-height: 24px;
                                        min-width: 24px;
                                        padding: 0;
                                        background-color: #c62828;
                                        border-radius: 50%;
                                    "
                                    (click)="onRemoveUser(user, post.partKey); $event.stopPropagation()"
                                >
                                    <mat-icon style="font-size: 18px; width: 18px; height: 18px; color: white">{{
                                        user?.isPending ? 'close' : 'remove'
                                    }}</mat-icon>
                                </button>
                            </span>
                        </div>
                    </div>

                    <!-- HFA: Held From Above — shown when post has NO own active users.
               In edit mode: hidden when post has pending users (shown directly).
               In view mode: always shown (pending users are hidden, so post appears vacant). -->
                    <div
                        class="ob-staff"
                        *ngIf="!hasActiveUsersOnPost(post) && shouldShowHfa2(post)"
                        style="margin-top: 2px; padding-left: 0"
                    >
                        <!-- HFA row: every holder's small avatar side by side, then ONE chip to the origin post (Nov 2026) -->
                        <div class="ob-hfa-row">
                            <ng-container *ngFor="let user of hfaAsUsers2(post)">
                                <span
                                    class="ob-avatar-wrap"
                                    style="cursor: pointer"
                                    (click)="
                                        navigateToHfaOrigin.emit(post?.heldFromAbovePartKey); $event.stopPropagation()
                                    "
                                    [matTooltip]="
                                        (user.name | titlecase) + ' (held from above) — click to go to their post'
                                    "
                                >
                                    <img
                                        [src]="getUserAvatarUrl(user)"
                                        [class.ob-online]="isUserOnline(user)"
                                        class="ob-hfa-avatar"
                                    />
                                    <span *ngIf="isUserOnline(user)" class="ob-online-dot"></span>
                                </span>
                            </ng-container>
                            <span
                                class="ob-hfa-link"
                                role="link"
                                (click)="navigateToHfaOrigin.emit(post?.heldFromAbovePartKey); $event.stopPropagation()"
                                [matTooltip]="
                                    (post?.linkedToName
                                        ? 'Linked to ' + post.linkedToName
                                        : 'Held from above by ' + (post?.heldFromAboveUserName | titlecase)) +
                                    ' — click to go to their post'
                                "
                                ><mat-icon>link</mat-icon>{{ post?.linkedToName ? 'Linked' : 'HFA' }}</span
                            >
                        </div>
                    </div>

                    <!-- Pending HFA: In edit mode, show pending users from ancestor as HFA -->
                    <div class="ob-staff" *ngIf="shouldShowPendingHfa2(post)" style="margin-top: 2px; padding-left: 0">
                        <div
                            *ngFor="let user of getPendingHfaUsers(post)"
                            style="display: flex; align-items: center; padding: 2px 0"
                        >
                            <span class="ob-avatar-wrap">
                                <img
                                    [src]="getUserAvatarUrl(user)"
                                    class="ob-pending-avatar"
                                    [class.ob-online]="isUserOnline(user)"
                                    style="object-fit: cover; border-radius: 50%"
                                />
                                <span
                                    class="ob-hfa-badge"
                                    style="color: #e65100"
                                    matTooltip="Pending Approval - Held From Above"
                                >
                                    <mat-icon>move_down</mat-icon>
                                </span>
                            </span>
                            <span
                                style="
                                    margin-left: 6px;
                                    font-size: 24px;
                                    font-weight: 500;
                                    line-height: 1.2;
                                    text-align: left;
                                    color: #795548;
                                "
                                >{{ user.name | titlecase }}</span
                            >
                            <span
                                style="
                                    margin-left: 4px;
                                    font-size: 0.7rem;
                                    background: #fff3e0;
                                    color: #e65100;
                                    padding: 1px 5px;
                                    border-radius: 8px;
                                    font-weight: 600;
                                "
                                >Pending</span
                            >
                        </div>
                    </div>
                </div>
            </div>
        </ng-template>

        <!-- ═══════════════════════════════════════════════════════════════════════════
         SECTION D → #sectionInDeptTpl (formerly: Sections In Department)
         ═══════════════════════════════════════════════════════════════════════════
         The nested organizational hierarchy within a department card:
           Department → Section → Unit → Post

         Each level renders:
           - Header with clickable name + context menu (edit actions)
           - VFP (Valuable Final Product) editor
           - Disabled user display (containers don't show users directly)
           - Child elements

         IMPORTANT: Only "Post" elements are droppable targets for user drag-drop.
         Sections and Units are containers — they are NEVER droppable.
         ═══════════════════════════════════════════════════════════════════════════ -->
        <ng-template #sectionInDeptTpl let-section>
            <div>
                <div
                    class="user-list-container"
                    style="margin: 4px 0"
                    [class.deleting-part]="isDeleting(section.partKey || section.key)"
                >
                    <!-- Sections are containers — NOT droppable. Only Posts are droppable. -->

                    <div class="section-hover-area">
                        <!-- ─── D1: Section Header ─── -->
                        <div
                            class="section-header-container"
                            style="display: flex; align-items: center; justify-content: center; margin-bottom: 0.5rem"
                        >
                            <span class="grouped" style="width: 100%; display: flex; justify-content: center">
                                <span
                                    class="clickable-span section-title"
                                    style="
                                        padding: 12px 16px;
                                        width: 98%;
                                        min-height: 48px;
                                        display: flex;
                                        align-items: center;
                                        justify-content: space-between;
                                        gap: 8px;
                                        border-radius: 8px;
                                        margin-top: 0;
                                        box-sizing: border-box;
                                    "
                                    (click)="
                                        openPositionTemplate.emit({ data: section, parentNode: node });
                                        $event.stopPropagation()
                                    "
                                >
                                    <span
                                        class="section-text"
                                        style="
                                            flex: 1;
                                            text-align: center;
                                            font-size: 22px;
                                            font-weight: 500;
                                            line-height: 1.4;
                                            word-wrap: break-word;
                                            overflow-wrap: break-word;
                                            min-width: 0;
                                        "
                                    >
                                        {{ section.name }}
                                        <span class="part-type-badge badge-section">Section</span>
                                    </span>

                                    <!-- Section context menu (edit mode only) -->
                                    <button
                                        *ngIf="isEditing"
                                        mat-icon-button
                                        color="accent"
                                        [matMenuTriggerFor]="sectionMenu"
                                        [disabled]="isDeleting(section.partKey || section.key)"
                                        class="ob-menu-btn"
                                        style="
                                            flex: 0 0 auto;
                                            margin: 0;
                                            width: 28px;
                                            height: 28px;
                                            border-radius: 50%;
                                            display: flex;
                                            align-items: center;
                                            justify-content: center;
                                            padding: 0;
                                        "
                                        (click)="$event.stopPropagation()"
                                    >
                                        <mat-icon
                                            class="ob-header-icon"
                                            style="font-size: 18px; font-weight: bold; line-height: 1; margin: 0"
                                            >more_vert</mat-icon
                                        >
                                    </button>

                                    <!-- Section actions menu — trialing simplified shape: Edit
                       takes the slot of the old "Manage Structure" submenu
                       trigger (kept commented inline so it can be flipped back). -->
                                    <mat-menu #sectionMenu="matMenu">
                                        <!-- TRIAL: "Manage Structure" submenu trigger temporarily
                         disabled. Uncomment the button below + the
                         #sectionStructureMenu definition further down to restore.
                    <button mat-menu-item [matMenuTriggerFor]="sectionStructureMenu">
                      <mat-icon>account_tree</mat-icon>
                      <span>Manage Structure</span>
                    </button>
                    -->
                                        <button mat-menu-item (click)="onEditSection(section)">
                                            <mat-icon>edit</mat-icon>
                                            <span>Edit Section</span>
                                        </button>
                                        <button
                                            mat-menu-item
                                            *ngIf="!hideStaff"
                                            (click)="bulkAssignStaff.emit(section)"
                                        >
                                            <mat-icon color="primary">group_add</mat-icon>
                                            <span>Assign Staff to Posts</span>
                                        </button>
                                        <mat-divider></mat-divider>
                                        <button mat-menu-item color="warn" (click)="onDeleteSection(section)">
                                            <mat-icon color="warn">delete</mat-icon>
                                            <span>Delete Section</span>
                                        </button>
                                    </mat-menu>
                                    <!-- Old Manage Structure submenu kept commented for easy restore:
                  <mat-menu #sectionStructureMenu="matMenu">
                    <button mat-menu-item color="primary" (click)="onAddSibling(section)">
                      <mat-icon color="primary">add</mat-icon>
                      <span>Add Sibling Section</span>
                    </button>
                    <button mat-menu-item color="accent" (click)="onAddChild(section)">
                      <mat-icon color="accent">add_circle</mat-icon>
                      <span>Add Child Unit</span>
                    </button>
                    <button mat-menu-item color="accent" (click)="onAddChildPost(section)">
                      <mat-icon color="accent">add_circle</mat-icon>
                      <span>Add Child Post</span>
                    </button>
                    <button mat-menu-item *ngIf="section?.unitsInSection?.length > 0" (click)="onReorderSectionChildren(section)">
                      <mat-icon>swap_vert</mat-icon>
                      <span>Reorder Children</span>
                    </button>
                  </mat-menu>
                  -->
                                </span>
                            </span>
                        </div>

                        <!-- ─── D1b: Section Header-Strip Posts (heads/deputies/assistants) ─── -->
                        <ng-container *ngIf="section?.headerPostsInSection?.length">
                            <div
                                class="ob-header-posts-strip"
                                style="
                                    display: flex;
                                    flex-wrap: wrap;
                                    gap: 6px;
                                    justify-content: center;
                                    padding: 4px 8px;
                                    margin: 0 6px 6px 6px;
                                    background: rgba(0, 0, 0, 0.04);
                                    border-radius: 8px;
                                "
                            >
                                <ng-container *ngFor="let hp of section.headerPostsInSection">
                                    <ng-container
                                        *ngTemplateOutlet="headerPostChip; context: { $implicit: hp }"
                                    ></ng-container>
                                </ng-container>
                            </div>
                        </ng-container>

                        <!-- ─── D2: Section VFP ─── -->
                        <!-- In DOM when VFP has text OR VFP editing toggle on; CSS .sub-part-vfp hides by default, hover reveals.
                 When `vfpAlwaysVisible` is set, the .vfp-pinned class overrides the hidden state so the VFP
                 stays visible on the read-only org board (no hover required). -->
                        <div
                            *ngIf="section?.vfp || isVfpEditingOn"
                            class="sub-part-vfp"
                            [class.sub-part-vfp-editing]="editingSubPartVfp[section.partKey || section.key]"
                            [class.vfp-pinned]="section?.vfpAlwaysVisible"
                            style="
                                padding: 8px 12px;
                                background-color: #777;
                                color: #eee;
                                border-radius: 4px;
                                display: flex;
                                align-items: stretch;
                                gap: 10px;
                            "
                            [style.cursor]="isVfpEditingOn ? 'pointer' : 'default'"
                            (click)="
                                isVfpEditingOn && !editingSubPartVfp[section.partKey || section.key]
                                    ? startEditingSubPartVfp(section.partKey || section.key)
                                    : null;
                                $event.stopPropagation()
                            "
                        >
                            <div
                                style="
                                    display: flex;
                                    flex-direction: column;
                                    align-items: center;
                                    gap: 0px;
                                    min-width: 30px;
                                "
                            >
                                <mat-icon style="font-size: 22px; width: 22px; height: 22px; opacity: 0.85"
                                    >emoji_events</mat-icon
                                >
                                <div
                                    style="
                                        font-size: 9px;
                                        text-transform: uppercase;
                                        letter-spacing: 0.5px;
                                        font-weight: 700;
                                    "
                                >
                                    VFP
                                </div>
                                <button
                                    *ngIf="isEditing && isVfpEditingOn"
                                    type="button"
                                    class="vfp-pin-btn"
                                    [class.vfp-pin-btn--active]="section?.vfpAlwaysVisible"
                                    [matTooltip]="
                                        section?.vfpAlwaysVisible
                                            ? 'Unpin VFP (return to hover-only)'
                                            : 'Pin VFP (always show on read-only board)'
                                    "
                                    (click)="toggleVfpAlwaysVisible(section?.partKey || section?.key, section, $event)"
                                >
                                    <mat-icon>push_pin</mat-icon>
                                </button>
                            </div>
                            <div style="flex: 1; min-width: 0">
                                <div
                                    *ngIf="!editingSubPartVfp[section.partKey || section.key]"
                                    style="
                                        font-size: 15px;
                                        line-height: 1.4;
                                        font-weight: 700;
                                        word-wrap: break-word;
                                        overflow-wrap: break-word;
                                        white-space: pre-wrap;
                                    "
                                    [style.font-style]="section?.vfp ? 'normal' : 'italic'"
                                    [style.opacity]="section?.vfp ? '1' : '0.6'"
                                >
                                    {{ section?.vfp || (isVfpEditingOn ? 'Click to add VFP...' : '') }}
                                </div>
                                <div *ngIf="editingSubPartVfp[section.partKey || section.key]" style="width: 100%">
                                    <textarea
                                        style="
                                            width: 100%;
                                            min-height: 90px;
                                            background: rgba(0, 0, 0, 0.25);
                                            border: 1px solid rgba(255, 255, 255, 0.3);
                                            border-radius: 4px;
                                            color: #eee;
                                            font-size: 22px;
                                            padding: 8px;
                                            resize: vertical;
                                            font-family: inherit;
                                            box-sizing: border-box;
                                            font-weight: 700;
                                            line-height: 1.4;
                                        "
                                        [value]="section?.vfp || ''"
                                        (blur)="
                                            onSubPartVfpBlur(
                                                section.partKey || section.key,
                                                $any($event.target).value,
                                                section
                                            )
                                        "
                                        (keydown)="
                                            onSubPartVfpKeydown(
                                                $event,
                                                section.partKey || section.key,
                                                $any($event.target),
                                                section
                                            )
                                        "
                                        placeholder="Describe the VFP..."
                                    ></textarea>
                                    <div style="font-size: 10px; opacity: 0.5; margin-top: 1px">
                                        Ctrl+Enter to save, Esc to cancel
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- /section-hover-area -->

                    <!-- Sections are containers — they do not show HFA (only Posts show HFA) -->

                    <!-- Sections are containers — never display users directly (only Posts show users) -->
                    <div *ngIf="false" style="margin-top: 0.5rem">
                        <div
                            *ngFor="let user of section?.userDetailsArray"
                            (click)="openUserTemplate.emit({ user: user, node: node }); $event.stopPropagation()"
                            class="section-user-item"
                            style="cursor: pointer"
                        >
                            <span
                                class="grouped"
                                style="
                                    display: flex;
                                    align-items: center;
                                    justify-content: space-between;
                                    padding-left: 8px;
                                    padding-bottom: 8px;
                                "
                            >
                                <span style="display: flex; align-items: center">
                                    <img
                                        style="width: 2rem; height: 2rem; border-radius: 50%; object-fit: cover"
                                        [src]="getUserAvatarUrl(user)"
                                    />
                                    <span class="clickable-span ob-user-name" style="margin-left: 2px">
                                        {{ user.name | titlecase }}</span
                                    >
                                </span>
                                <button
                                    *ngIf="isEditing"
                                    mat-icon-button
                                    matTooltip="Remove staff member from section"
                                    style="
                                        width: 24px;
                                        height: 24px;
                                        line-height: 24px;
                                        min-width: 24px;
                                        padding: 0;
                                        background-color: #c62828;
                                        border-radius: 50%;
                                    "
                                    (click)="onRemoveUser(user, section.partKey); $event.stopPropagation()"
                                >
                                    <mat-icon style="font-size: 18px; width: 18px; height: 18px; color: white">{{
                                        user?.isPending ? 'close' : 'remove'
                                    }}</mat-icon>
                                </button>
                            </span>
                        </div>
                    </div>

                    <!-- ──────────────────────────────────────────────────────────────
               Section body-children dispatch — interleaves posts + units in path
               order. Per-item templates (#postInSectionTpl, #unitInSectionTpl) defined
               below. Replaces the legacy `postsInSection` / `unitsInSection` split.
               ────────────────────────────────────────────────────────────── -->
                    <ng-container *ngIf="section?.bodyChildren?.length">
                        <ng-container *ngFor="let child of section.bodyChildren">
                            <!-- margin 0: the post box's own 4px margins are the gap (was 8px + 4px each side, Nov 2026) -->
                            <div *ngIf="child.partType === 'post'" class="user-list-container" style="margin: 0">
                                <ng-container
                                    [ngTemplateOutlet]="postInSectionTpl"
                                    [ngTemplateOutletContext]="{ $implicit: child, section: section }"
                                ></ng-container>
                            </div>
                            <ng-container
                                *ngIf="child.partType === 'unit'"
                                [ngTemplateOutlet]="unitInSectionTpl"
                                [ngTemplateOutletContext]="{ $implicit: child, section: section }"
                            ></ng-container>
                        </ng-container>
                    </ng-container>

                    <!-- ─── D3 → per-item template: Post directly in section (formerly D3 body). ─── -->
                    <ng-template #postInSectionTpl let-post let-section="section">
                        <div
                            class="droppable-part ob-post-card"
                            [class.deleting-part]="isDeleting(post.partKey || post.key)"
                            [style.border-left-color]="node?.bc || '#90a4ae'"
                            style="
                                width: 96%;
                                align-self: center;
                                margin: 4px 0;
                                padding: 4px 6px;
                                box-sizing: border-box;
                            "
                            [attr.data-part-key]="post.key"
                            [attr.data-part-type]="'post'"
                            [attr.data-part-name]="post.name"
                            [attr.data-parent-section-key]="section.key"
                            [attr.data-parent-dept-key]="node?.data?.key"
                        >
                            <!-- Post title + menu -->
                            <div class="post-hover-area">
                                <div style="display: flex; align-items: center; gap: 4px">
                                    <div
                                        class="clickable-span ob-post-name"
                                        style="
                                            flex: 1;
                                            font-size: 24px;
                                            font-weight: 700;
                                            line-height: 1.2;
                                            padding: 4px 8px;
                                            cursor: pointer;
                                            word-wrap: break-word;
                                            overflow-wrap: break-word;
                                            text-align: center;
                                        "
                                        (click)="
                                            openPositionTemplate.emit({ data: post, parentNode: node });
                                            $event.stopPropagation()
                                        "
                                    >
                                        {{ post.name }}
                                    </div>
                                    <button
                                        *ngIf="isEditing"
                                        mat-icon-button
                                        [matMenuTriggerFor]="postInSectionMenu"
                                        [disabled]="isDeleting(post.partKey || post.key)"
                                        class="ob-menu-btn"
                                        style="width: 24px; height: 24px; min-width: 24px; padding: 0"
                                        (click)="$event.stopPropagation()"
                                    >
                                        <mat-icon style="font-size: 16px; width: 16px; height: 16px"
                                            >more_vert</mat-icon
                                        >
                                    </button>
                                    <mat-menu #postInSectionMenu="matMenu">
                                        <!-- TRIAL: "Add Sibling Post" temporarily hidden from the post menu.
                         Uncomment the button below to restore it.
                    <button mat-menu-item (click)="onAddSiblingPost(post)">
                      <mat-icon color="primary">add</mat-icon>
                      <span>Add Sibling Post</span>
                    </button>
                    -->
                                        <button mat-menu-item (click)="onEditPost(post)">
                                            <mat-icon>edit</mat-icon>
                                            <span>Edit Post</span>
                                        </button>
                                        <button mat-menu-item *ngIf="!hideStaff" (click)="bulkAssignStaff.emit(post)">
                                            <mat-icon color="primary">group_add</mat-icon>
                                            <span>Assign Staff to Post</span>
                                        </button>
                                        <button mat-menu-item (click)="dialogService.openPostHatsManagerDialog(post)">
                                            <mat-icon style="color: var(--entity-color-hats, #1093ad)">school</mat-icon>
                                            <span>Manage Post Hats</span>
                                        </button>
                                        <mat-divider></mat-divider>
                                        <button mat-menu-item (click)="onDeletePost(post)">
                                            <mat-icon color="warn">delete</mat-icon>
                                            <span>Delete Post</span>
                                        </button>
                                    </mat-menu>
                                </div>

                                <!-- Post VFP (postsInSection) -->
                                <!-- In DOM when VFP has text OR VFP editing toggle on; CSS .sub-part-vfp hides by default, hover reveals.
                     `.vfp-pinned` overrides the hidden state when the VFP is pinned (always visible). -->
                                <div
                                    *ngIf="post?.vfp || isVfpEditingOn"
                                    class="sub-part-vfp"
                                    [class.sub-part-vfp-editing]="editingSubPartVfp[post.partKey || post.key]"
                                    [class.vfp-pinned]="post?.vfpAlwaysVisible"
                                    style="
                                        padding: 6px 10px;
                                        background-color: #777;
                                        color: #eee;
                                        border-radius: 4px;
                                        display: flex;
                                        align-items: stretch;
                                        gap: 8px;
                                        margin: 4px 8px;
                                    "
                                    [style.cursor]="isVfpEditingOn ? 'pointer' : 'default'"
                                    (click)="
                                        isVfpEditingOn && !editingSubPartVfp[post.partKey || post.key]
                                            ? startEditingSubPartVfp(post.partKey || post.key)
                                            : null;
                                        $event.stopPropagation()
                                    "
                                >
                                    <div
                                        style="
                                            display: flex;
                                            flex-direction: column;
                                            align-items: center;
                                            gap: 0px;
                                            min-width: 26px;
                                        "
                                    >
                                        <mat-icon style="font-size: 18px; width: 18px; height: 18px; opacity: 0.85"
                                            >emoji_events</mat-icon
                                        >
                                        <div
                                            style="
                                                font-size: 8px;
                                                text-transform: uppercase;
                                                letter-spacing: 0.5px;
                                                font-weight: 700;
                                            "
                                        >
                                            VFP
                                        </div>
                                        <button
                                            *ngIf="isEditing && isVfpEditingOn"
                                            type="button"
                                            class="vfp-pin-btn vfp-pin-btn--sm"
                                            [class.vfp-pin-btn--active]="post?.vfpAlwaysVisible"
                                            [matTooltip]="
                                                post?.vfpAlwaysVisible
                                                    ? 'Unpin VFP (return to hover-only)'
                                                    : 'Pin VFP (always show on read-only board)'
                                            "
                                            (click)="toggleVfpAlwaysVisible(post?.partKey || post?.key, post, $event)"
                                        >
                                            <mat-icon>push_pin</mat-icon>
                                        </button>
                                    </div>
                                    <div style="flex: 1; min-width: 0">
                                        <div
                                            *ngIf="!editingSubPartVfp[post.partKey || post.key]"
                                            style="
                                                font-size: 14px;
                                                line-height: 1.4;
                                                font-weight: 700;
                                                word-wrap: break-word;
                                                overflow-wrap: break-word;
                                                white-space: pre-wrap;
                                            "
                                            [style.font-style]="post?.vfp ? 'normal' : 'italic'"
                                            [style.opacity]="post?.vfp ? '1' : '0.6'"
                                        >
                                            {{ post?.vfp || (isVfpEditingOn ? 'Click to add VFP...' : '') }}
                                        </div>
                                        <div *ngIf="editingSubPartVfp[post.partKey || post.key]" style="width: 100%">
                                            <textarea
                                                style="
                                                    width: 100%;
                                                    min-height: 90px;
                                                    background: rgba(0, 0, 0, 0.25);
                                                    border: 1px solid rgba(255, 255, 255, 0.3);
                                                    border-radius: 4px;
                                                    color: #eee;
                                                    font-size: 14px;
                                                    padding: 6px;
                                                    resize: vertical;
                                                    font-family: inherit;
                                                    box-sizing: border-box;
                                                    font-weight: 700;
                                                    line-height: 1.4;
                                                "
                                                [value]="post?.vfp || ''"
                                                (blur)="
                                                    onSubPartVfpBlur(
                                                        post.partKey || post.key,
                                                        $any($event.target).value,
                                                        post
                                                    )
                                                "
                                                (keydown)="
                                                    onSubPartVfpKeydown(
                                                        $event,
                                                        post.partKey || post.key,
                                                        $any($event.target),
                                                        post
                                                    )
                                                "
                                                placeholder="Describe the VFP..."
                                            ></textarea>
                                            <div style="font-size: 9px; opacity: 0.5; margin-top: 1px">
                                                Ctrl+Enter to save, Esc to cancel
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- /post-hover-area -->

                            <!-- Users assigned to this post -->
                            <div
                                class="ob-staff"
                                *ngIf="getVisibleUsersForPost(post).length > 0"
                                style="margin-top: 2px; padding-left: 0"
                            >
                                <div
                                    *ngFor="let user of getVisibleUsersForPost(post)"
                                    [class.ob-pending-row]="user?.isPending"
                                    [class.processing-user]="isProcessingUser(user?.key, post.partKey || post.key)"
                                    style="
                                        display: flex;
                                        align-items: center;
                                        justify-content: space-between;
                                        padding: 2px 0;
                                    "
                                >
                                    <span style="display: flex; align-items: center">
                                        <span *ngIf="user?.isPending" class="ob-pending-avatar-wrap">
                                            <img
                                                [src]="getUserAvatarUrl(user)"
                                                class="ob-pending-avatar"
                                                [class.ob-online]="isUserOnline(user)"
                                                style="object-fit: cover; border-radius: 50%; flex-shrink: 0"
                                            />
                                            <span *ngIf="isUserOnline(user)" class="ob-online-dot"></span>
                                        </span>
                                        <span *ngIf="!user?.isPending" class="ob-avatar-wrap">
                                            <img
                                                [src]="getUserAvatarUrl(user)"
                                                [class.ob-online]="isUserOnline(user)"
                                                style="
                                                    width: 2.5rem;
                                                    height: 2.5rem;
                                                    object-fit: cover;
                                                    border-radius: 50%;
                                                    flex-shrink: 0;
                                                "
                                            />
                                            <span *ngIf="isUserOnline(user)" class="ob-online-dot"></span>
                                        </span>
                                        <span
                                            class="clickable-span"
                                            (click)="
                                                openUserTemplate.emit({ user: user, node: node });
                                                $event.stopPropagation()
                                            "
                                            style="
                                                margin-left: 6px;
                                                font-size: 24px;
                                                font-weight: 500;
                                                line-height: 1.2;
                                                text-align: left;
                                            "
                                        >
                                            {{ user.name | titlecase }}
                                            <span
                                                *ngIf="user?.isPending && !user?.awaitingAcceptance"
                                                class="ob-pending-badge"
                                                matTooltip="Awaiting admin approval on this post"
                                                >Pending</span
                                            >
                                            <span
                                                *ngIf="user?.awaitingAcceptance"
                                                class="ob-pending-badge ob-invited-badge"
                                                matTooltip="Invited — awaiting user to accept the invitation. Admin cannot approve until accepted."
                                                >Invited</span
                                            >
                                        </span>
                                    </span>
                                    <span class="ob-user-actions">
                                        <button
                                            *ngIf="isEditing && user?.isPending && !user?.awaitingAcceptance"
                                            mat-icon-button
                                            matTooltip="Approve assignment"
                                            style="
                                                width: 24px;
                                                height: 24px;
                                                line-height: 24px;
                                                min-width: 24px;
                                                padding: 0;
                                                background-color: #2e7d32;
                                                border-radius: 50%;
                                            "
                                            (click)="
                                                approveUser.emit({ user: user, node: node, post: post });
                                                $event.stopPropagation()
                                            "
                                        >
                                            <mat-icon style="font-size: 18px; width: 18px; height: 18px; color: white"
                                                >check</mat-icon
                                            >
                                        </button>
                                        <button
                                            *ngIf="isEditing"
                                            mat-icon-button
                                            matTooltip="Remove staff member from post"
                                            style="
                                                width: 24px;
                                                height: 24px;
                                                line-height: 24px;
                                                min-width: 24px;
                                                padding: 0;
                                                background-color: #c62828;
                                                border-radius: 50%;
                                            "
                                            (click)="onRemoveUser(user, post.partKey); $event.stopPropagation()"
                                        >
                                            <mat-icon
                                                style="font-size: 18px; width: 18px; height: 18px; color: white"
                                                >{{ user?.isPending ? 'close' : 'remove' }}</mat-icon
                                            >
                                        </button>
                                    </span>
                                </div>
                            </div>

                            <!-- HFA for postsInSection -->
                            <div
                                class="ob-staff"
                                *ngIf="!hasActiveUsersOnPost(post) && shouldShowHfa2(post)"
                                style="margin-top: 2px; padding-left: 0"
                            >
                                <!-- HFA row: every holder's small avatar side by side, then ONE chip to the origin post (Nov 2026) -->
                                <div class="ob-hfa-row">
                                    <ng-container *ngFor="let user of hfaAsUsers2(post)">
                                        <span
                                            class="ob-avatar-wrap"
                                            style="cursor: pointer"
                                            (click)="
                                                navigateToHfaOrigin.emit(post?.heldFromAbovePartKey);
                                                $event.stopPropagation()
                                            "
                                            [matTooltip]="
                                                (user.name | titlecase) +
                                                ' (held from above) — click to go to their post'
                                            "
                                        >
                                            <img
                                                [src]="getUserAvatarUrl(user)"
                                                [class.ob-online]="isUserOnline(user)"
                                                class="ob-hfa-avatar"
                                            />
                                            <span *ngIf="isUserOnline(user)" class="ob-online-dot"></span>
                                        </span>
                                    </ng-container>
                                    <span
                                        class="ob-hfa-link"
                                        role="link"
                                        (click)="
                                            navigateToHfaOrigin.emit(post?.heldFromAbovePartKey);
                                            $event.stopPropagation()
                                        "
                                        [matTooltip]="
                                            (post?.linkedToName
                                                ? 'Linked to ' + post.linkedToName
                                                : 'Held from above by ' + (post?.heldFromAboveUserName | titlecase)) +
                                            ' — click to go to their post'
                                        "
                                        ><mat-icon>link</mat-icon>{{ post?.linkedToName ? 'Linked' : 'HFA' }}</span
                                    >
                                </div>
                            </div>

                            <!-- Pending HFA for postsInSection -->
                            <div
                                class="ob-staff"
                                *ngIf="shouldShowPendingHfa2(post)"
                                style="margin-top: 2px; padding-left: 0"
                            >
                                <div
                                    *ngFor="let user of getPendingHfaUsers(post)"
                                    style="display: flex; align-items: center; padding: 2px 0"
                                >
                                    <span class="ob-avatar-wrap">
                                        <img
                                            [src]="getUserAvatarUrl(user)"
                                            class="ob-pending-avatar"
                                            [class.ob-online]="isUserOnline(user)"
                                            style="object-fit: cover; border-radius: 50%"
                                        />
                                        <span
                                            class="ob-hfa-badge"
                                            style="color: #e65100"
                                            matTooltip="Pending Approval - Held From Above"
                                        >
                                            <mat-icon>move_down</mat-icon>
                                        </span>
                                    </span>
                                    <span
                                        style="
                                            margin-left: 6px;
                                            font-size: 24px;
                                            font-weight: 500;
                                            line-height: 1.2;
                                            text-align: left;
                                            color: #795548;
                                        "
                                        >{{ user.name | titlecase }}</span
                                    >
                                    <span
                                        style="
                                            margin-left: 4px;
                                            font-size: 0.7rem;
                                            background: #fff3e0;
                                            color: #e65100;
                                            padding: 1px 5px;
                                            border-radius: 8px;
                                            font-weight: 600;
                                        "
                                        >Pending</span
                                    >
                                </div>
                            </div>
                        </div>
                    </ng-template>

                    <!-- ─── D4 → per-item template: Unit in section (formerly D4 body). ─── -->
                    <ng-template #unitInSectionTpl let-unit let-section="section">
                        <div class="user-list-container" [class.deleting-part]="isDeleting(unit.partKey || unit.key)">
                            <!-- Units are containers — NOT droppable. Only Posts are droppable. -->
                            <div
                                class="ob-unit-container"
                                style="
                                    padding: 8px 0;
                                    width: 96%;
                                    line-height: 2;
                                    display: flex;
                                    flex-direction: column;
                                    align-self: center;
                                    margin: 0 auto;
                                    border-radius: 2px;
                                    text-align: center;
                                "
                                [style.background-color]="getLightTint(node?.bc ?? undefined)"
                            >
                                <div class="unit-hover-area">
                                    <!-- Unit header + context menu -->
                                    <span
                                        class="grouped"
                                        style="display: block; font-size: 16px; font-weight: bold; text-align: center"
                                    >
                                        <span
                                            class="clickable-span ob-unit-title"
                                            style="
                                                padding: 12px 10px;
                                                width: 100%;
                                                min-height: 48px;
                                                display: flex;
                                                align-items: center;
                                                justify-content: space-between;
                                                gap: 8px;
                                                border-radius: 8px;
                                                border-width: 1px;
                                                border-style: solid;
                                                box-sizing: border-box;
                                            "
                                            (click)="
                                                openPositionTemplate.emit({ data: unit, parentNode: node });
                                                $event.stopPropagation()
                                            "
                                        >
                                            <span
                                                style="
                                                    flex: 1;
                                                    text-align: center;
                                                    font-size: 18px;
                                                    font-weight: bold;
                                                    line-height: 1.4;
                                                    word-wrap: break-word;
                                                    overflow-wrap: break-word;
                                                    min-width: 0;
                                                "
                                            >
                                                {{ unit.name | titlecase }}
                                                <span class="part-type-badge badge-unit">Unit</span>
                                            </span>

                                            <!-- Unit context menu (edit mode only) -->
                                            <button
                                                *ngIf="isEditing"
                                                mat-icon-button
                                                color="accent"
                                                [matMenuTriggerFor]="unitMenu"
                                                [disabled]="isDeleting(unit.partKey || unit.key)"
                                                class="ob-menu-btn"
                                                style="
                                                    flex: 0 0 auto;
                                                    margin: 0;
                                                    width: 24px;
                                                    height: 24px;
                                                    min-width: 24px;
                                                    border-radius: 50%;
                                                    display: flex;
                                                    align-items: center;
                                                    justify-content: center;
                                                    padding: 0;
                                                "
                                                (click)="$event.stopPropagation()"
                                            >
                                                <mat-icon
                                                    class="ob-header-icon"
                                                    style="
                                                        font-size: 16px;
                                                        width: 16px;
                                                        height: 16px;
                                                        line-height: 16px;
                                                        font-weight: bold;
                                                        margin: 0;
                                                    "
                                                    >more_vert</mat-icon
                                                >
                                            </button>

                                            <!-- Unit actions menu — trialing simplified shape (see
                           sectionMenu above for rationale). -->
                                            <mat-menu #unitMenu="matMenu">
                                                <!-- TRIAL: "Manage Structure" submenu trigger temporarily
                             disabled. Uncomment the button below + the
                             #unitStructureMenu definition further down to restore.
                        <button mat-menu-item [matMenuTriggerFor]="unitStructureMenu">
                          <mat-icon>account_tree</mat-icon>
                          <span>Manage Structure</span>
                        </button>
                        -->
                                                <button mat-menu-item (click)="onEditUnit(unit)">
                                                    <mat-icon>edit</mat-icon>
                                                    <span>Edit Unit</span>
                                                </button>
                                                <button
                                                    mat-menu-item
                                                    *ngIf="!hideStaff"
                                                    (click)="bulkAssignStaff.emit(unit)"
                                                >
                                                    <mat-icon color="primary">group_add</mat-icon>
                                                    <span>Assign Staff to Posts</span>
                                                </button>
                                                <mat-divider></mat-divider>
                                                <button mat-menu-item color="warn" (click)="onDeleteUnit(unit)">
                                                    <mat-icon color="warn">delete</mat-icon>
                                                    <span>Delete Unit</span>
                                                </button>
                                            </mat-menu>
                                            <!-- Old Manage Structure submenu kept commented for easy restore:
                      <mat-menu #unitStructureMenu="matMenu">
                        <button mat-menu-item color="primary" (click)="onAddSiblingUnit(unit)">
                          <mat-icon color="primary">add</mat-icon>
                          <span>Add Sibling Unit</span>
                        </button>
                        <button mat-menu-item color="accent" (click)="onAddChildPost(unit)">
                          <mat-icon color="accent">add_circle</mat-icon>
                          <span>Add Child Post</span>
                        </button>
                        <button mat-menu-item *ngIf="unit?.postsInUnit?.length > 0" (click)="onReorderUnitChildren(unit)">
                          <mat-icon>swap_vert</mat-icon>
                          <span>Reorder Children</span>
                        </button>
                      </mat-menu>
                      -->
                                        </span>
                                    </span>

                                    <!-- Unit VFP -->
                                    <!-- In DOM when VFP has text OR VFP editing toggle on; CSS .sub-part-vfp hides by default, hover reveals.
                       `.vfp-pinned` overrides the hidden state when the VFP is pinned (always visible). -->
                                    <div
                                        *ngIf="unit?.vfp || isVfpEditingOn"
                                        class="sub-part-vfp"
                                        [class.sub-part-vfp-editing]="editingSubPartVfp[unit.partKey || unit.key]"
                                        [class.vfp-pinned]="unit?.vfpAlwaysVisible"
                                        style="
                                            padding: 8px 12px;
                                            background-color: #777;
                                            color: #eee;
                                            border-radius: 4px;
                                            display: flex;
                                            align-items: stretch;
                                            gap: 10px;
                                        "
                                        [style.cursor]="isVfpEditingOn ? 'pointer' : 'default'"
                                        (click)="
                                            isVfpEditingOn && !editingSubPartVfp[unit.partKey || unit.key]
                                                ? startEditingSubPartVfp(unit.partKey || unit.key)
                                                : null;
                                            $event.stopPropagation()
                                        "
                                    >
                                        <div
                                            style="
                                                display: flex;
                                                flex-direction: column;
                                                align-items: center;
                                                gap: 0px;
                                                min-width: 30px;
                                            "
                                        >
                                            <mat-icon style="font-size: 22px; width: 22px; height: 22px; opacity: 0.85"
                                                >emoji_events</mat-icon
                                            >
                                            <div
                                                style="
                                                    font-size: 9px;
                                                    text-transform: uppercase;
                                                    letter-spacing: 0.5px;
                                                    font-weight: 700;
                                                "
                                            >
                                                VFP
                                            </div>
                                            <button
                                                *ngIf="isEditing && isVfpEditingOn"
                                                type="button"
                                                class="vfp-pin-btn"
                                                [class.vfp-pin-btn--active]="unit?.vfpAlwaysVisible"
                                                [matTooltip]="
                                                    unit?.vfpAlwaysVisible
                                                        ? 'Unpin VFP (return to hover-only)'
                                                        : 'Pin VFP (always show on read-only board)'
                                                "
                                                (click)="
                                                    toggleVfpAlwaysVisible(unit?.partKey || unit?.key, unit, $event)
                                                "
                                            >
                                                <mat-icon>push_pin</mat-icon>
                                            </button>
                                        </div>
                                        <div style="flex: 1; min-width: 0">
                                            <div
                                                *ngIf="!editingSubPartVfp[unit.partKey || unit.key]"
                                                style="
                                                    font-size: 15px;
                                                    line-height: 1.4;
                                                    font-weight: 700;
                                                    word-wrap: break-word;
                                                    overflow-wrap: break-word;
                                                    white-space: pre-wrap;
                                                "
                                                [style.font-style]="unit?.vfp ? 'normal' : 'italic'"
                                                [style.opacity]="unit?.vfp ? '1' : '0.6'"
                                            >
                                                {{ unit?.vfp || (isVfpEditingOn ? 'Click to add VFP...' : '') }}
                                            </div>
                                            <div
                                                *ngIf="editingSubPartVfp[unit.partKey || unit.key]"
                                                style="width: 100%"
                                            >
                                                <textarea
                                                    style="
                                                        width: 100%;
                                                        min-height: 90px;
                                                        background: rgba(0, 0, 0, 0.25);
                                                        border: 1px solid rgba(255, 255, 255, 0.3);
                                                        border-radius: 4px;
                                                        color: #eee;
                                                        font-size: 22px;
                                                        padding: 8px;
                                                        resize: vertical;
                                                        font-family: inherit;
                                                        box-sizing: border-box;
                                                        font-weight: 700;
                                                        line-height: 1.4;
                                                    "
                                                    [value]="unit?.vfp || ''"
                                                    (blur)="
                                                        onSubPartVfpBlur(
                                                            unit.partKey || unit.key,
                                                            $any($event.target).value,
                                                            unit
                                                        )
                                                    "
                                                    (keydown)="
                                                        onSubPartVfpKeydown(
                                                            $event,
                                                            unit.partKey || unit.key,
                                                            $any($event.target),
                                                            unit
                                                        )
                                                    "
                                                    placeholder="Describe the VFP..."
                                                ></textarea>
                                                <div style="font-size: 10px; opacity: 0.5; margin-top: 1px">
                                                    Ctrl+Enter to save, Esc to cancel
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <!-- /unit-hover-area -->

                                <!-- Units are containers — they do not show HFA (only Posts show HFA) -->

                                <!-- Units are containers — never display users directly (only Posts show users) -->
                                <div *ngIf="false" style="margin-top: 0.5rem">
                                    <div
                                        *ngFor="let user of unit?.userDetailsArray"
                                        (click)="
                                            openUserTemplate.emit({ user: user, node: node }); $event.stopPropagation()
                                        "
                                        class="unit-user-item"
                                        style="cursor: pointer"
                                    >
                                        <span
                                            class="grouped"
                                            style="
                                                display: flex;
                                                align-items: center;
                                                justify-content: space-between;
                                                padding-left: 8px;
                                                padding-bottom: 8px;
                                            "
                                        >
                                            <span style="display: flex; align-items: center">
                                                <img
                                                    style="
                                                        width: 2rem;
                                                        height: 2rem;
                                                        border-radius: 50%;
                                                        object-fit: cover;
                                                    "
                                                    [src]="getUserAvatarUrl(user)"
                                                />
                                                <span class="clickable-span ob-user-name" style="margin-left: 2px">
                                                    {{ user.name | titlecase }}</span
                                                >
                                            </span>
                                            <button
                                                *ngIf="isEditing"
                                                mat-icon-button
                                                matTooltip="Remove staff member from unit"
                                                style="
                                                    width: 24px;
                                                    height: 24px;
                                                    line-height: 24px;
                                                    min-width: 24px;
                                                    padding: 0;
                                                    background-color: #c62828;
                                                    border-radius: 50%;
                                                "
                                                (click)="onRemoveUser(user, unit.partKey); $event.stopPropagation()"
                                            >
                                                <mat-icon
                                                    style="font-size: 18px; width: 18px; height: 18px; color: white"
                                                    >{{ user?.isPending ? 'close' : 'remove' }}</mat-icon
                                                >
                                            </button>
                                        </span>
                                    </div>
                                </div>

                                <!-- ─── D5: Posts In Unit (postsInUnit) ───
                     The deepest nesting level. Each post has:
                       - Post title + context menu
                       - Post VFP
                       - User avatars (if assigned)
                       - HFA indication (if no own users) -->
                                <div
                                    *ngIf="unit?.postsInUnit && unit?.postsInUnit.length > 0"
                                    class="user-list-container"
                                >
                                    <div
                                        *ngFor="let post of unit?.postsInUnit"
                                        class="droppable-part ob-post-card"
                                        [class.deleting-part]="isDeleting(post.partKey || post.key)"
                                        [style.border-left-color]="node?.bc || '#90a4ae'"
                                        style="margin: 4px 0; padding: 4px 6px; box-sizing: border-box"
                                        [attr.data-part-key]="post.key"
                                        [attr.data-part-type]="'post'"
                                        [attr.data-part-name]="post.name"
                                        [attr.data-parent-unit-key]="unit.key"
                                        [attr.data-parent-section-key]="section.key"
                                        [attr.data-parent-dept-key]="node?.data?.key"
                                    >
                                        <div class="post-hover-area">
                                            <!-- Post title + menu -->
                                            <div style="display: flex; align-items: center; gap: 4px">
                                                <div
                                                    class="clickable-span ob-post-name"
                                                    style="
                                                        flex: 1;
                                                        font-size: 24px;
                                                        font-weight: 700;
                                                        line-height: 1.2;
                                                        padding: 4px 8px;
                                                        cursor: pointer;
                                                        word-wrap: break-word;
                                                        overflow-wrap: break-word;
                                                        text-align: center;
                                                    "
                                                    (click)="
                                                        openPositionTemplate.emit({ data: post, parentNode: node });
                                                        $event.stopPropagation()
                                                    "
                                                >
                                                    {{ post.name }}
                                                </div>
                                                <button
                                                    *ngIf="isEditing"
                                                    mat-icon-button
                                                    [matMenuTriggerFor]="postInUnitMenu"
                                                    [disabled]="isDeleting(post.partKey || post.key)"
                                                    class="ob-menu-btn"
                                                    style="width: 24px; height: 24px; min-width: 24px; padding: 0"
                                                    (click)="$event.stopPropagation()"
                                                >
                                                    <mat-icon style="font-size: 16px; width: 16px; height: 16px"
                                                        >more_vert</mat-icon
                                                    >
                                                </button>
                                                <mat-menu #postInUnitMenu="matMenu">
                                                    <!-- TRIAL: "Add Sibling Post" temporarily hidden from the post menu.
                               Uncomment the button below to restore it.
                          <button mat-menu-item (click)="onAddSiblingPost(post)">
                            <mat-icon color="primary">add</mat-icon>
                            <span>Add Sibling Post</span>
                          </button>
                          -->
                                                    <button mat-menu-item (click)="onEditPost(post)">
                                                        <mat-icon>edit</mat-icon>
                                                        <span>Edit Post</span>
                                                    </button>
                                                    <button
                                                        mat-menu-item
                                                        *ngIf="!hideStaff"
                                                        (click)="bulkAssignStaff.emit(post)"
                                                    >
                                                        <mat-icon color="primary">group_add</mat-icon>
                                                        <span>Assign Staff to Post</span>
                                                    </button>
                                                    <button
                                                        mat-menu-item
                                                        (click)="dialogService.openPostHatsManagerDialog(post)"
                                                    >
                                                        <mat-icon style="color: var(--entity-color-hats, #1093ad)"
                                                            >school</mat-icon
                                                        >
                                                        <span>Manage Post Hats</span>
                                                    </button>
                                                    <mat-divider></mat-divider>
                                                    <button mat-menu-item (click)="onDeletePost(post)">
                                                        <mat-icon color="warn">delete</mat-icon>
                                                        <span>Delete Post</span>
                                                    </button>
                                                </mat-menu>
                                            </div>

                                            <!-- Post VFP (postsInUnit) -->
                                            <!-- In DOM when VFP has text OR VFP editing toggle on; CSS .sub-part-vfp hides by default, hover reveals.
                           `.vfp-pinned` overrides the hidden state when the VFP is pinned (always visible). -->
                                            <div
                                                *ngIf="post?.vfp || isVfpEditingOn"
                                                class="sub-part-vfp"
                                                [class.sub-part-vfp-editing]="
                                                    editingSubPartVfp[post.partKey || post.key]
                                                "
                                                [class.vfp-pinned]="post?.vfpAlwaysVisible"
                                                style="
                                                    padding: 6px 10px;
                                                    background-color: #777;
                                                    color: #eee;
                                                    border-radius: 4px;
                                                    display: flex;
                                                    align-items: stretch;
                                                    gap: 8px;
                                                    margin: 4px 8px;
                                                "
                                                [style.cursor]="isVfpEditingOn ? 'pointer' : 'default'"
                                                (click)="
                                                    isVfpEditingOn && !editingSubPartVfp[post.partKey || post.key]
                                                        ? startEditingSubPartVfp(post.partKey || post.key)
                                                        : null;
                                                    $event.stopPropagation()
                                                "
                                            >
                                                <div
                                                    style="
                                                        display: flex;
                                                        flex-direction: column;
                                                        align-items: center;
                                                        gap: 0px;
                                                        min-width: 26px;
                                                    "
                                                >
                                                    <mat-icon
                                                        style="
                                                            font-size: 18px;
                                                            width: 18px;
                                                            height: 18px;
                                                            opacity: 0.85;
                                                        "
                                                        >emoji_events</mat-icon
                                                    >
                                                    <div
                                                        style="
                                                            font-size: 8px;
                                                            text-transform: uppercase;
                                                            letter-spacing: 0.5px;
                                                            font-weight: 700;
                                                        "
                                                    >
                                                        VFP
                                                    </div>
                                                    <button
                                                        *ngIf="isEditing && isVfpEditingOn"
                                                        type="button"
                                                        class="vfp-pin-btn vfp-pin-btn--sm"
                                                        [class.vfp-pin-btn--active]="post?.vfpAlwaysVisible"
                                                        [matTooltip]="
                                                            post?.vfpAlwaysVisible
                                                                ? 'Unpin VFP (return to hover-only)'
                                                                : 'Pin VFP (always show on read-only board)'
                                                        "
                                                        (click)="
                                                            toggleVfpAlwaysVisible(
                                                                post?.partKey || post?.key,
                                                                post,
                                                                $event
                                                            )
                                                        "
                                                    >
                                                        <mat-icon>push_pin</mat-icon>
                                                    </button>
                                                </div>
                                                <div style="flex: 1; min-width: 0">
                                                    <div
                                                        *ngIf="!editingSubPartVfp[post.partKey || post.key]"
                                                        style="
                                                            font-size: 14px;
                                                            line-height: 1.4;
                                                            font-weight: 700;
                                                            word-wrap: break-word;
                                                            overflow-wrap: break-word;
                                                            white-space: pre-wrap;
                                                        "
                                                        [style.font-style]="post?.vfp ? 'normal' : 'italic'"
                                                        [style.opacity]="post?.vfp ? '1' : '0.6'"
                                                    >
                                                        {{ post?.vfp || (isVfpEditingOn ? 'Click to add VFP...' : '') }}
                                                    </div>
                                                    <div
                                                        *ngIf="editingSubPartVfp[post.partKey || post.key]"
                                                        style="width: 100%"
                                                    >
                                                        <textarea
                                                            style="
                                                                width: 100%;
                                                                min-height: 90px;
                                                                background: rgba(0, 0, 0, 0.25);
                                                                border: 1px solid rgba(255, 255, 255, 0.3);
                                                                border-radius: 4px;
                                                                color: #eee;
                                                                font-size: 14px;
                                                                padding: 6px;
                                                                resize: vertical;
                                                                font-family: inherit;
                                                                box-sizing: border-box;
                                                                font-weight: 700;
                                                                line-height: 1.4;
                                                            "
                                                            [value]="post?.vfp || ''"
                                                            (blur)="
                                                                onSubPartVfpBlur(
                                                                    post.partKey || post.key,
                                                                    $any($event.target).value,
                                                                    post
                                                                )
                                                            "
                                                            (keydown)="
                                                                onSubPartVfpKeydown(
                                                                    $event,
                                                                    post.partKey || post.key,
                                                                    $any($event.target),
                                                                    post
                                                                )
                                                            "
                                                            placeholder="Describe the VFP..."
                                                        ></textarea>
                                                        <div style="font-size: 9px; opacity: 0.5; margin-top: 1px">
                                                            Ctrl+Enter to save, Esc to cancel
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <!-- /post-hover-area -->

                                        <!-- Users assigned to this post -->
                                        <div
                                            class="ob-staff"
                                            *ngIf="getVisibleUsersForPost(post).length > 0"
                                            style="margin-top: 2px; padding-left: 0"
                                        >
                                            <div
                                                *ngFor="let user of getVisibleUsersForPost(post)"
                                                [class.ob-pending-row]="user?.isPending"
                                                [class.processing-user]="
                                                    isProcessingUser(user?.key, post.partKey || post.key)
                                                "
                                                style="
                                                    display: flex;
                                                    align-items: center;
                                                    justify-content: space-between;
                                                    padding: 2px 0;
                                                "
                                            >
                                                <span style="display: flex; align-items: center">
                                                    <span *ngIf="user?.isPending" class="ob-pending-avatar-wrap">
                                                        <img
                                                            [src]="getUserAvatarUrl(user)"
                                                            class="ob-pending-avatar"
                                                            [class.ob-online]="isUserOnline(user)"
                                                            style="
                                                                object-fit: cover;
                                                                border-radius: 50%;
                                                                flex-shrink: 0;
                                                            "
                                                        />
                                                        <span *ngIf="isUserOnline(user)" class="ob-online-dot"></span>
                                                    </span>
                                                    <span *ngIf="!user?.isPending" class="ob-avatar-wrap">
                                                        <img
                                                            [src]="getUserAvatarUrl(user)"
                                                            [class.ob-online]="isUserOnline(user)"
                                                            style="
                                                                width: 2.5rem;
                                                                height: 2.5rem;
                                                                object-fit: cover;
                                                                border-radius: 50%;
                                                                flex-shrink: 0;
                                                            "
                                                        />
                                                        <span *ngIf="isUserOnline(user)" class="ob-online-dot"></span>
                                                    </span>
                                                    <span
                                                        class="clickable-span"
                                                        (click)="
                                                            openUserTemplate.emit({ user: user, node: node });
                                                            $event.stopPropagation()
                                                        "
                                                        style="
                                                            margin-left: 6px;
                                                            font-size: 24px;
                                                            font-weight: 500;
                                                            line-height: 1.2;
                                                            text-align: left;
                                                        "
                                                    >
                                                        {{ user.name | titlecase }}
                                                        <span
                                                            *ngIf="user?.isPending && !user?.awaitingAcceptance"
                                                            class="ob-pending-badge"
                                                            matTooltip="Awaiting admin approval on this post"
                                                            >Pending</span
                                                        >
                                                        <span
                                                            *ngIf="user?.awaitingAcceptance"
                                                            class="ob-pending-badge ob-invited-badge"
                                                            matTooltip="Invited — awaiting user to accept the invitation. Admin cannot approve until accepted."
                                                            >Invited</span
                                                        >
                                                    </span>
                                                </span>
                                                <span class="ob-user-actions">
                                                    <button
                                                        *ngIf="
                                                            isEditing && user?.isPending && !user?.awaitingAcceptance
                                                        "
                                                        mat-icon-button
                                                        matTooltip="Approve assignment"
                                                        style="
                                                            width: 24px;
                                                            height: 24px;
                                                            line-height: 24px;
                                                            min-width: 24px;
                                                            padding: 0;
                                                            background-color: #2e7d32;
                                                            border-radius: 50%;
                                                        "
                                                        (click)="
                                                            approveUser.emit({ user: user, node: node, post: post });
                                                            $event.stopPropagation()
                                                        "
                                                    >
                                                        <mat-icon
                                                            style="
                                                                font-size: 18px;
                                                                width: 18px;
                                                                height: 18px;
                                                                color: white;
                                                            "
                                                            >check</mat-icon
                                                        >
                                                    </button>
                                                    <button
                                                        *ngIf="isEditing"
                                                        mat-icon-button
                                                        matTooltip="Remove staff member from post"
                                                        style="
                                                            width: 24px;
                                                            height: 24px;
                                                            line-height: 24px;
                                                            min-width: 24px;
                                                            padding: 0;
                                                            background-color: #c62828;
                                                            border-radius: 50%;
                                                        "
                                                        (click)="
                                                            onRemoveUser(user, post.partKey); $event.stopPropagation()
                                                        "
                                                    >
                                                        <mat-icon
                                                            style="
                                                                font-size: 18px;
                                                                width: 18px;
                                                                height: 18px;
                                                                color: white;
                                                            "
                                                            >{{ user?.isPending ? 'close' : 'remove' }}</mat-icon
                                                        >
                                                    </button>
                                                </span>
                                            </div>
                                        </div>

                                        <!-- HFA for postsInUnit -->
                                        <div
                                            class="ob-staff"
                                            *ngIf="!hasActiveUsersOnPost(post) && shouldShowHfa2(post)"
                                            style="margin-top: 2px; padding-left: 0"
                                        >
                                            <!-- HFA row: every holder's small avatar side by side, then ONE chip to the origin post (Nov 2026) -->
                                            <div class="ob-hfa-row">
                                                <ng-container *ngFor="let user of hfaAsUsers2(post)">
                                                    <span
                                                        class="ob-avatar-wrap"
                                                        style="cursor: pointer"
                                                        (click)="
                                                            navigateToHfaOrigin.emit(post?.heldFromAbovePartKey);
                                                            $event.stopPropagation()
                                                        "
                                                        [matTooltip]="
                                                            (user.name | titlecase) +
                                                            ' (held from above) — click to go to their post'
                                                        "
                                                    >
                                                        <img
                                                            [src]="getUserAvatarUrl(user)"
                                                            [class.ob-online]="isUserOnline(user)"
                                                            class="ob-hfa-avatar"
                                                        />
                                                        <span *ngIf="isUserOnline(user)" class="ob-online-dot"></span>
                                                    </span>
                                                </ng-container>
                                                <span
                                                    class="ob-hfa-link"
                                                    role="link"
                                                    (click)="
                                                        navigateToHfaOrigin.emit(post?.heldFromAbovePartKey);
                                                        $event.stopPropagation()
                                                    "
                                                    [matTooltip]="
                                                        (post?.linkedToName
                                                            ? 'Linked to ' + post.linkedToName
                                                            : 'Held from above by ' +
                                                              (post?.heldFromAboveUserName | titlecase)) +
                                                        ' — click to go to their post'
                                                    "
                                                    ><mat-icon>link</mat-icon
                                                    >{{ post?.linkedToName ? 'Linked' : 'HFA' }}</span
                                                >
                                            </div>
                                        </div>

                                        <!-- Pending HFA for postsInUnit -->
                                        <div
                                            class="ob-staff"
                                            *ngIf="shouldShowPendingHfa2(post)"
                                            style="margin-top: 2px; padding-left: 0"
                                        >
                                            <div
                                                *ngFor="let user of getPendingHfaUsers(post)"
                                                style="display: flex; align-items: center; padding: 2px 0"
                                            >
                                                <span class="ob-avatar-wrap">
                                                    <img
                                                        [src]="getUserAvatarUrl(user)"
                                                        class="ob-pending-avatar"
                                                        [class.ob-online]="isUserOnline(user)"
                                                        style="object-fit: cover; border-radius: 50%"
                                                    />
                                                    <span
                                                        class="ob-hfa-badge"
                                                        style="color: #e65100"
                                                        matTooltip="Pending Approval - Held From Above"
                                                    >
                                                        <mat-icon>move_down</mat-icon>
                                                    </span>
                                                </span>
                                                <span
                                                    style="
                                                        margin-left: 6px;
                                                        font-size: 24px;
                                                        font-weight: 500;
                                                        line-height: 1.2;
                                                        text-align: left;
                                                        color: #795548;
                                                    "
                                                    >{{ user.name | titlecase }}</span
                                                >
                                                <span
                                                    style="
                                                        margin-left: 4px;
                                                        font-size: 0.7rem;
                                                        background: #fff3e0;
                                                        color: #e65100;
                                                        padding: 1px 5px;
                                                        border-radius: 8px;
                                                        font-weight: 600;
                                                    "
                                                    >Pending</span
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </ng-template>
                </div>
            </div>
        </ng-template>
    </mat-card-content>

    <!-- ═══════════════════════════════════════════════════════════════════════════
       SECTION E: Node-Level VFP (Valuable Final Product)
       ═══════════════════════════════════════════════════════════════════════════
       Displayed at the bottom of the card for all part types EXCEPT divisions
       (divisions get a spanning VFP rendered at the board level instead).

       Supports inline editing:
         - Click to start editing (edit mode only)
         - Ctrl+Enter to save
         - Esc to cancel
         - Click away (blur) to save
       ═══════════════════════════════════════════════════════════════════════════ -->
    <!-- In DOM when VFP has text OR VFP editing toggle on (non-divisions only); CSS .vfp-hover-only hides by default, card hover reveals.
       `.vfp-pinned` overrides .vfp-hover-only so pinned VFPs render permanently on the read-only board. -->
    <div
        *ngIf="(nodeAsAny?.data?.vfp || isVfpEditingOn) && nodeAsAny?.data?.partType !== 'division'"
        class="vfp-section"
        [class.vfp-hover-only]="nodeAsAny?.data?.partType !== 'department'"
        [class.vfp-pinned]="nodeAsAny?.data?.vfpAlwaysVisible"
        [class.vfp-editing]="isEditingVfp"
        style="
            margin-top: auto;
            padding: 10px 14px;
            background-color: #777;
            color: #eee;
            border-radius: 0 0 4px 4px;
            display: flex;
            align-items: stretch;
            gap: 12px;
            cursor: pointer;
        "
        (click)="isVfpEditingOn ? startEditingVfp() : null; $event.stopPropagation()"
    >
        <div
            style="
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 0px;
                padding-right: 4px;
                min-width: 36px;
            "
        >
            <mat-icon style="font-size: 28px; width: 28px; height: 28px; opacity: 0.85">emoji_events</mat-icon>
            <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 700">VFP</div>
            <button
                *ngIf="isEditing && isVfpEditingOn"
                type="button"
                class="vfp-pin-btn vfp-pin-btn--lg"
                [class.vfp-pin-btn--active]="nodeAsAny?.data?.vfpAlwaysVisible"
                [matTooltip]="
                    nodeAsAny?.data?.vfpAlwaysVisible
                        ? 'Unpin VFP (return to hover-only)'
                        : 'Pin VFP (always show on read-only board)'
                "
                (click)="
                    toggleVfpAlwaysVisible(nodeAsAny?.data?.partKey || nodeAsAny?.data?.key, nodeAsAny?.data, $event)
                "
            >
                <mat-icon>push_pin</mat-icon>
            </button>
        </div>
        <div style="flex: 1; min-width: 0">
            <div
                *ngIf="!isEditingVfp"
                style="
                    font-size: 16px;
                    line-height: 1.45;
                    font-weight: 700;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    white-space: pre-wrap;
                "
                [style.cursor]="isVfpEditingOn ? 'pointer' : 'default'"
                [style.font-style]="nodeAsAny?.data?.vfp ? 'normal' : 'italic'"
                [style.opacity]="nodeAsAny?.data?.vfp ? '1' : '0.6'"
            >
                {{ nodeAsAny?.data?.vfp || (isVfpEditingOn ? 'Click to add VFP...' : '') }}
            </div>
            <div *ngIf="isEditingVfp" style="width: 100%">
                <textarea
                    #vfpTextarea
                    style="
                        width: 100%;
                        min-height: 120px;
                        background: rgba(0, 0, 0, 0.25);
                        border: 1px solid rgba(255, 255, 255, 0.3);
                        border-radius: 4px;
                        color: #eee;
                        font-size: 16px;
                        padding: 10px;
                        resize: vertical;
                        font-family: inherit;
                        box-sizing: border-box;
                        font-weight: 700;
                        line-height: 1.45;
                    "
                    [value]="nodeAsAny?.data?.vfp || ''"
                    (blur)="onVfpBlur(vfpTextarea.value)"
                    (keydown)="onVfpKeydown($event, vfpTextarea)"
                    placeholder="Describe the Valuable Final Product of this position..."
                ></textarea>
                <div style="font-size: 11px; opacity: 0.5; margin-top: 2px">
                    Ctrl+Enter to save, Esc to cancel, click away to save
                </div>
            </div>
        </div>
    </div>
</mat-card>

<!-- ════════════════════════════════════════════════════════════════════════════
     Reusable: Header-Post Chip
     ════════════════════════════════════════════════════════════════════════════
     Renders a compact pill for a post that has been promoted into a container's
     header strip (head / deputy / assistant). Shows the assigned user's avatar
     + name (or "Unoccupied"), the role badge, and the post name. Click opens
     the post detail.
     ════════════════════════════════════════════════════════════════════════════ -->
<ng-template #headerPostChip let-hp>
    <!-- droppable-part + full attr set so the board's onDrop DOM walk accepts
       staff drops on header posts here too (ISS-001). -->
    <span
        class="ob-header-post-chip clickable-span droppable-part"
        [attr.data-part-key]="hp?.partKey || hp?.key"
        [attr.data-part-type]="'post'"
        [attr.data-part-name]="hp?.name"
        [attr.data-parent-dept-key]="node?.data?.key"
        [attr.data-header-role]="hp?.headerRole"
        (click)="openPositionTemplate.emit({ data: hp, parentNode: node }); $event.stopPropagation()"
        style="
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 6px 10px 6px 6px;
            border-radius: 12px;
            background: #fff;
            color: #222;
            border: 1px solid rgba(0, 0, 0, 0.12);
            line-height: 1.2;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
            cursor: pointer;
            width: 100%;
            box-sizing: border-box;
            text-align: left;
        "
    >
        <ng-container *ngIf="(getVisibleUsersForPost(hp) || [])[0] as hpUser; else noChipUser">
            <img
                class="ob-staff"
                [src]="getUserAvatarUrl(hpUser)"
                alt=""
                style="width: 44px; height: 44px; border-radius: 50%; object-fit: cover; flex-shrink: 0"
            />
            <span style="display: flex; flex-direction: column; gap: 2px; line-height: 1.2; min-width: 0; flex: 1">
                <!-- Role tag + post name on one line, like the department head chips (wall-TV pass, Nov 2026) -->
                <span
                    class="ob-chip-post-line"
                    style="display: flex; align-items: center; flex-wrap: wrap; gap: 2px 6px; line-height: 1.25"
                >
                    <span
                        style="
                            flex: 0 0 auto;
                            text-transform: uppercase;
                            font-size: 14px;
                            font-weight: 700;
                            letter-spacing: 0.6px;
                            color: #fff;
                            background: #4a5568;
                            padding: 1px 7px;
                            border-radius: 4px;
                            line-height: 1.4;
                        "
                        >{{ hp?.headerRole }}</span
                    >
                    <span style="font-size: 20px; font-weight: 700; overflow-wrap: anywhere">{{ hp?.name }}</span>
                </span>
                <span class="ob-staff" style="font-weight: 500; font-size: 24px; color: #111; overflow-wrap: anywhere">
                    {{ hpUser?.name }}
                    <!-- ISS-008: flag additional occupants instead of hiding them. -->
                    <span
                        *ngIf="(getVisibleUsersForPost(hp) || []).length > 1"
                        [title]="'Multiple people are assigned to this header post'"
                        style="
                            display: inline-block;
                            background: #fff3e0;
                            border: 1px solid #f0ad4e;
                            color: #8a5a00;
                            border-radius: 8px;
                            padding: 0 6px;
                            font-size: 11px;
                            font-weight: 700;
                            margin-left: 4px;
                        "
                    >
                        +{{ (getVisibleUsersForPost(hp) || []).length - 1 }}
                    </span>
                </span>
                <!-- ISS-005: header posts never showed their VFP — standard reveal
             (chip hover / pin / board-level Show VFPs toggle). -->
                <span
                    *ngIf="hp?.vfp"
                    class="sub-part-vfp"
                    [class.vfp-pinned]="hp?.vfpAlwaysVisible"
                    style="
                        font-size: 12px;
                        line-height: 1.3;
                        color: #444;
                        white-space: pre-wrap;
                        word-break: break-word;
                        text-align: left;
                    "
                >
                    <strong>VFP:</strong> {{ hp.vfp }}
                </span>
            </span>
            <!-- Edit-mode staff actions: the same approve/remove pair a body post row
           carries (#postInParentTpl above). Section header chips had none. -->
            <span
                *ngIf="isEditing"
                class="ob-user-actions ob-staff"
                (click)="$event.stopPropagation()"
                style="margin-left: 4px"
            >
                <button
                    *ngIf="hpUser?.isPending && !hpUser?.awaitingAcceptance"
                    mat-icon-button
                    matTooltip="Approve assignment"
                    style="
                        width: 24px;
                        height: 24px;
                        line-height: 24px;
                        min-width: 24px;
                        padding: 0;
                        background-color: #2e7d32;
                        border-radius: 50%;
                    "
                    (click)="approveUser.emit({ user: hpUser, node: node, post: hp }); $event.stopPropagation()"
                >
                    <mat-icon style="font-size: 18px; width: 18px; height: 18px; color: white">check</mat-icon>
                </button>
                <button
                    mat-icon-button
                    matTooltip="Remove staff member from post"
                    style="
                        width: 24px;
                        height: 24px;
                        line-height: 24px;
                        min-width: 24px;
                        padding: 0;
                        background-color: #c62828;
                        border-radius: 50%;
                    "
                    (click)="onRemoveUser(hpUser, hp?.partKey || hp?.key); $event.stopPropagation()"
                >
                    <mat-icon style="font-size: 18px; width: 18px; height: 18px; color: white">{{
                        hpUser?.isPending ? 'close' : 'remove'
                    }}</mat-icon>
                </button>
            </span>
        </ng-container>
        <ng-template #noChipUser>
            <!-- Held from above: the holders' small avatars + the HFA link chip, as in body posts. -->
            <ng-container *ngIf="!hasActiveUsersOnPost(hp) && hfaAsUsers2(hp).length; else chipEmpty">
                <span class="ob-staff" style="display: inline-flex; gap: 2px; flex-shrink: 0">
                    <img
                        *ngFor="let user of hfaAsUsers2(hp)"
                        [src]="getUserAvatarUrl(user)"
                        alt=""
                        class="ob-hfa-avatar"
                        style="width: 28px; height: 28px"
                    />
                </span>
                <span style="display: flex; flex-direction: column; gap: 4px; line-height: 1.2; min-width: 0; flex: 1">
                    <!-- Role tag + post name on one line, like the department head chips (wall-TV pass, Nov 2026) -->
                    <span
                        class="ob-chip-post-line"
                        style="display: flex; align-items: center; flex-wrap: wrap; gap: 2px 6px; line-height: 1.25"
                    >
                        <span
                            style="
                                flex: 0 0 auto;
                                text-transform: uppercase;
                                font-size: 14px;
                                font-weight: 700;
                                letter-spacing: 0.6px;
                                color: #fff;
                                background: #4a5568;
                                padding: 1px 7px;
                                border-radius: 4px;
                                line-height: 1.4;
                            "
                            >{{ hp?.headerRole }}</span
                        >
                        <span style="font-size: 20px; font-weight: 700; overflow-wrap: anywhere">{{ hp?.name }}</span>
                    </span>
                    <span
                        class="ob-hfa-link ob-staff"
                        role="link"
                        style="align-self: flex-start; margin-left: 0"
                        (click)="navigateToHfaOrigin.emit(hp?.heldFromAbovePartKey); $event.stopPropagation()"
                        [matTooltip]="
                            'Held from above by ' +
                            (hp?.heldFromAboveUserName | titlecase) +
                            ' — click to go to their post'
                        "
                        ><mat-icon>link</mat-icon>HFA</span
                    >
                </span>
            </ng-container>
            <ng-template #chipEmpty>
                <span
                    class="ob-staff"
                    style="
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        width: 44px;
                        height: 44px;
                        border-radius: 50%;
                        background: rgba(0, 0, 0, 0.1);
                        color: rgba(0, 0, 0, 0.4);
                        font-size: 12px;
                        flex-shrink: 0;
                    "
                    >—</span
                >
                <span style="display: flex; flex-direction: column; gap: 2px; line-height: 1.2; min-width: 0; flex: 1">
                    <!-- Role tag + post name on one line, like the department head chips (wall-TV pass, Nov 2026) -->
                    <span
                        class="ob-chip-post-line"
                        style="display: flex; align-items: center; flex-wrap: wrap; gap: 2px 6px; line-height: 1.25"
                    >
                        <span
                            style="
                                flex: 0 0 auto;
                                text-transform: uppercase;
                                font-size: 14px;
                                font-weight: 700;
                                letter-spacing: 0.6px;
                                color: #fff;
                                background: #4a5568;
                                padding: 1px 7px;
                                border-radius: 4px;
                                line-height: 1.4;
                            "
                            >{{ hp?.headerRole }}</span
                        >
                        <span style="font-size: 20px; font-weight: 700; overflow-wrap: anywhere">{{ hp?.name }}</span>
                    </span>
                    <span class="ob-staff" style="font-style: italic; font-weight: 500; font-size: 18px; opacity: 0.65"
                        >Unoccupied</span
                    >
                </span>
            </ng-template>
        </ng-template>
    </span>
</ng-template>
